import { Header } from "./-components/header"
import { useEffect } from "react"
import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/(site)/code-of-conduct")({
  component: CodeOfConduct,
})

function CodeOfConduct() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 max-w-4xl">
        <article className="prose prose-slate dark:prose-invert max-w-none">
          <h1>Knowledge Facts Ladder (KFL) — Code of Conduct</h1>
          <p className="text-muted-foreground">Last updated: 03.12.2025</p>

          <p>
            The purpose of this Code of Conduct is to ensure that all
            participants in the Knowledge Facts Ladder ("KFL", "the League")
            enjoy a fair, respectful, and secure competitive environment. By
            participating in KFL events or using KFL platforms, you agree to
            follow this Code.
          </p>
          <p>
            Violations may result in temporary suspension, permanent bans, or
            other disciplinary action at the discretion of KFL administration.
          </p>

          <h2>1. Fair Play and Integrity</h2>
          <p>1.1. All players must compete honestly and independently.</p>
          <p>1.2. The following actions are strictly prohibited:</p>
          <ul>
            <li>using search engines, notes, or external sources</li>
            <li>
              receiving assistance from any person or group during individual
              competitions
            </li>
            <li>using AI tools or automated systems</li>
            <li>
              manipulating time limits or attempting to pause or bypass them
            </li>
            <li>falsifying or tampering with gameplay recordings</li>
            <li>using multiple accounts</li>
          </ul>
          <p>
            1.3. Players may not exploit technical issues or platform weaknesses
            to gain advantage.
          </p>
          <p>
            1.4. Any attempt to cheat, mislead, or manipulate results may lead
            to an immediate and permanent ban.
          </p>

          <h2>2. Behavior in Games and Community Spaces</h2>
          <p>
            2.1. All players must conduct themselves respectfully toward other
            participants and KFL staff.
          </p>
          <p>2.2. The following behaviors result in immediate ban:</p>
          <ul>
            <li>harassment, bullying, or targeted insults</li>
            <li>hate speech or discriminatory language</li>
            <li>threats, intimidation, or incitement</li>
            <li>persistent toxic or disruptive behavior</li>
          </ul>
          <p>
            2.3. Heated competitive discussion is acceptable, but personal
            attacks and hostility are not.
          </p>
          <p>
            2.4. Inappropriate or disruptive behavior during games, livestreams,
            or community discussions may result in removal from events or
            permanent exclusion.
          </p>

          <h2>3. Communication in Group Chats and Team Environments</h2>
          <p>
            3.1. Group chats (including Discord, WhatsApp, Telegram, or
            KFL-hosted channels) must be used responsibly and respectfully.
          </p>
          <p>
            3.2. Spamming, excessive off-topic posting, or derailing
            conversations is not permitted.
          </p>
          <p>
            3.3. Team captains are responsible for maintaining a respectful
            environment within their teams.
          </p>
          <p>
            3.4. Sharing screenshots or personal score summaries is allowed only
            if they do not reveal protected quiz content or affect fairness.
          </p>

          <h2>4. Recording, Cameras, and Monitoring</h2>
          <p>
            4.1. For Pairs and Teams formats, players must keep their camera
            turned on for the entire duration of the game.
          </p>
          <p>
            4.2. Certain events may be recorded randomly or when cheating is
            suspected.
          </p>
          <p>
            4.3. By participating, players consent to the recording and
            potential publication of their gameplay as described in the Terms &
            Conditions.
          </p>
          <p>4.4. Attempts to obstruct or falsify recordings are prohibited.</p>

          <h2>5. Respect for Intellectual Property</h2>
          <p>
            5.1. All KFL quiz materials (questions, answers, formats,
            recordings) are protected by intellectual property laws.
          </p>
          <p>5.2. The following actions are prohibited:</p>
          <ul>
            <li>reposting or sharing quiz content outside KFL</li>
            <li>uploading proprietary files</li>
            <li>using KFL questions in other leagues or competitions</li>
            <li>using KFL content to train AI models</li>
          </ul>
          <p>5.3. Screenshots may not reveal protected questions or answers.</p>

          <h2>6. Team and Pairs Integrity</h2>
          <p>
            6.1. Team and Pairs events require fair collaboration only among
            participants registered for the match.
          </p>
          <p>
            6.2. Outside observers, shadow participants, or unregistered players
            are not permitted.
          </p>
          <p>
            6.3. All players must follow event-specific rules communicated by
            KFL.
          </p>

          <h2>7. Compliance With KFL Decisions</h2>
          <p>
            7.1. All decisions regarding bans, disqualifications, or result
            adjustments are made at the sole discretion of KFL.
          </p>
          <p>7.2. All decisions are final and cannot be appealed.</p>
          <p>
            7.3. Attempting to pressure, harass, or threaten KFL management
            regarding disciplinary decisions will result in permanent removal.
          </p>

          <h2>8. Reporting Misconduct</h2>
          <p>
            8.1. Players are encouraged to report cheating, misconduct, or
            harassment.
          </p>
          <p>
            8.2. Reports must be made in good faith; knowingly submitting false
            reports is a violation of this Code.
          </p>
          <p>
            8.3. KFL handles reports confidentially and takes appropriate
            action.
          </p>

          <h2>9. Consequences of Violations</h2>
          <p>9.1. Consequences may include:</p>
          <ul>
            <li>warning</li>
            <li>score invalidation</li>
            <li>removal from an event</li>
            <li>temporary suspension</li>
            <li>permanent ban</li>
          </ul>
          <p>
            9.2. Severe violations (cheating, harassment, threats) result in
            immediate permanent ban.
          </p>
          <p>
            9.3. KFL reserves the right to remove any player who undermines the
            fairness or integrity of the League.
          </p>

          <h2>10. Acceptance</h2>
          <p>
            By participating in KFL, joining any KFL-hosted chat or team, or
            accessing league content, you confirm that you have read,
            understood, and agree to this Code of Conduct.
          </p>

          <h2>11. Eligibility and Prior Misconduct</h2>
          <p>
            11.1. KFL reserves the right to refuse participation, deny
            registration, or remove any player from the League if there is
            credible evidence or reasonable belief that the player has engaged
            in cheating, harassment, toxic behavior, or other inappropriate
            conduct — whether inside KFL or in any other quiz league,
            tournament, or competitive community.
          </p>
          <p>
            11.2. This includes documented incidents, public reports, community
            feedback, or misconduct observed in unrelated leagues or events.
          </p>
          <p>
            11.3. KFL is not required to disclose evidence, sources, or details
            used to make these decisions.
          </p>
          <p>
            11.4. All decisions made under this clause are final and cannot be
            appealed.
          </p>
        </article>
      </main>
    </div>
  )
}
